import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  eachDayOfInterval,
  format,
  getMonth,
  getYear,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
} from "date-fns";
import {
  DATE_TIME_SELECTOR_MONTHS,
  DATE_TIME_SELECTOR_WEEKDAYS,
} from "../assets/constants/Constants";
import CircularProgressLoader from "./CircularProgressLoader";
import { capitalizeWords } from "../lib/Utils";

const groupBookingsByDay = (bookings) => {
  const bookingsByDay = {};
  (bookings || []).forEach((booking) => {
    if (!booking?.reserve_start) {
      return;
    }
    const startDate = startOfDay(new Date(booking.reserve_start));
    const endDate = booking.reserve_end ? startOfDay(new Date(booking.reserve_end)) : startDate;
    const spannedDays = endDate >= startDate
      ? eachDayOfInterval({ start: startDate, end: endDate })
      : [startDate];

    spannedDays.forEach((day) => {
      const dayKey = format(day, "yyyy-MM-dd");
      if (!bookingsByDay[dayKey]) {
        bookingsByDay[dayKey] = [];
      }
      bookingsByDay[dayKey].push(booking);
    });
  });
  return bookingsByDay;
};

const isMultiDayBooking = (booking) =>
  !isSameDay(new Date(booking.reserve_start), new Date(booking.reserve_end));

const formatBookingTimeRange = (booking) => {
  const start = new Date(booking.reserve_start);
  const end = new Date(booking.reserve_end);

  if (!isMultiDayBooking(booking)) {
    return `${format(start, "h:mm a")} – ${format(end, "h:mm a")}`;
  }

  return `${format(start, "MMM d, h:mm a")} – ${format(end, "MMM d, h:mm a")}`;
};

const ScheduleCalendarComponent = ({
  visible,
  onClose,
  bookings = [],
  lockerName = "",
  onDateChange,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const today = useMemo(() => new Date(), []);
  const [viewDate, setViewDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(today);

  const bookingsByDay = useMemo(() => groupBookingsByDay(bookings), [bookings]);

  const minBoundary = useMemo(() => startOfDay(today), [today]);

  const viewYear = getYear(viewDate);
  const viewMonth = getMonth(viewDate);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isPrevMonthDisabled = () => {
    const previousMonthLastDate = new Date(viewYear, viewMonth, 0);
    return previousMonthLastDate < minBoundary;
  };

  const isDatePast = (date) => date < minBoundary && !isSameDay(date, minBoundary);

  const prevMonth = () => {
    if (isPrevMonthDisabled()) return;
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const hasBookingsOnDay = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    return Boolean(bookingsByDay[format(date, "yyyy-MM-dd")]?.length);
  };

  const selectedDayBookings = bookingsByDay[format(selectedDate, "yyyy-MM-dd")] || [];

  const getStatusLabel = (status) => {
    switch (String(status).toUpperCase()) {
      case "BOOKED":
        return t("schedule_calendar_status_booked");
      case "DONE":
        return t("schedule_calendar_status_done");
      default:
        return status || "";
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="schedule-calendar-overlay" onClick={onClose}>
      {isLoading && <CircularProgressLoader message={t("pleaseWaitText")} />}
      <div className="schedule-calendar-modal" onClick={(e) => e.stopPropagation()}>
        <div className="schedule-calendar-header">
          <h3 className="schedule-calendar-title">
            {lockerName
              ? t("schedule_calendar_title_with_name", { lockerName })
              : t("schedule_calendar_title")}
          </h3>
          <button
            type="button"
            className="schedule-calendar-close"
            onClick={onClose}
            aria-label={t("schedule_calendar_close_label")}
          >
            ×
          </button>
        </div>

        <div className="calendar-card">
          <div className="calendar-header">
            <button type="button" className="calendar-nav" onClick={prevMonth} disabled={isPrevMonthDisabled()}>‹</button>
            <span className="calendar-month">{DATE_TIME_SELECTOR_MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" className="calendar-nav" onClick={nextMonth}>›</button>
          </div>

          <div className="calendar-weekdays">
            {DATE_TIME_SELECTOR_WEEKDAYS.map((weekday) => (
              <div key={weekday} className="calendar-weekday">{weekday}</div>
            ))}
          </div>

          <div className="calendar-days">
            {cells.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="calendar-day empty" />;
              }

              const date = new Date(viewYear, viewMonth, day);
              const isPast = isDatePast(date);
              const isSelected = isSameDay(date, selectedDate) && isSameMonth(date, selectedDate);
              const isCurrentDay = isToday(date);
              const hasBookings = hasBookingsOnDay(day);

              let dayClass = "calendar-day";
              if (isPast) dayClass += " past";
              else if (isSelected) dayClass += " selected";
              else if (isCurrentDay) dayClass += " today";
              if (hasBookings) dayClass += " has-booking";

              return (
                <button
                  type="button"
                  key={day}
                  className={dayClass}
                  onClick={() => {
                    if (isPast || isLoading) {
                      return;
                    }
                    setSelectedDate(date);
                    onDateChange?.(format(date, "yyyy-MM-dd"));
                  }}
                  disabled={isPast || isLoading}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        <div className="schedule-list-section">
          <h4 className="schedule-list-date">{format(selectedDate, "EEEE, MMM d")}</h4>

          {selectedDayBookings.length > 0 ? (
            <div className="schedule-list">
              {selectedDayBookings.map((booking, index) => (
                <div className="schedule-list-item" key={`${booking.locker_bank_id}-${index}`}>
                  <div className="schedule-list-item-info">
                    <span className="schedule-list-item-name">
                      {booking.locker_bank_name || booking.locker_bank_id}
                    </span>
                    <span className="schedule-list-item-time">
                      {formatBookingTimeRange(booking)}
                    </span>
                  </div>
                  <span className={`schedule-status-badge status-${String(booking.status).toLowerCase()}`}>
                    {capitalizeWords(getStatusLabel(booking.status))}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="schedule-list-empty">{t("schedule_calendar_no_bookings_text")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleCalendarComponent;
